/**
 * Utility functions for handling phone numbers
 */

/**
 * Sanitizes a phone number for use in tel: links
 * Removes all non-numeric characters except the plus sign
 * @param phone - The phone number string to sanitize
 * @returns The sanitized phone number string
 */
export const sanitizePhoneNumber = (
    phone: string | null | undefined,
): string => {
    if (!phone) {
        return "";
    }
    // Remove all characters except numbers and the plus sign
    return phone.replace(/[^0-9+]/g, "");
};

/**
 * Formats a phone number for display in the UI
 * @param phone - The phone number string to format
 * @returns Formatted phone number string (e.g., "(444) 444-4444")
 * @example
 * formatPhoneNumber("4444444444") // "(444) 444-4444"
 * formatPhoneNumber("14444444444") // "+1 (444) 444-4444"
 * formatPhoneNumber("+33123456789") // "+33 123 456 789"
 */
export const formatPhoneNumber = (
    phone: string | null | undefined,
): string => {
    if (!phone) {
        return "";
    }

    // Sanitize the phone number first
    const sanitized = sanitizePhoneNumber(phone);

    if (!sanitized) {
        return "";
    }

    // Handle US/Canada numbers (10 digits)
    if (sanitized.length === 10) {
        return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6)}`;
    }

    // Handle US/Canada numbers with country code 1 (11 digits starting with 1)
    if (sanitized.length === 11 && sanitized.startsWith("1")) {
        return `+1 (${sanitized.slice(1, 4)}) ${sanitized.slice(4, 7)}-${sanitized.slice(7)}`;
    }

    // Handle international numbers with + prefix
    if (sanitized.startsWith("+")) {
        // Try to detect country code length
        // Common 1-digit codes: 1 (US/Canada)
        // Common 2-digit codes: 33 (France), 44 (UK), 49 (Germany), 61 (Australia), 81 (Japan), 86 (China)
        // Common 3-digit codes: 852 (Hong Kong), 971 (UAE)
        let countryCode: string;
        let number: string;

        // Check for 1-digit country code (only +1 for North America)
        if (sanitized.startsWith("+1") && sanitized.length >= 12) {
            // This is already handled above, but just in case
            countryCode = "1";
            number = sanitized.slice(2);
        } else {
            // Default to 2-digit country code for most international numbers
            countryCode = sanitized.slice(1, 3);
            number = sanitized.slice(3);
        }

        // Add spacing every 3 digits for readability
        const spacedNumber = number.replace(/(\d{3})(?=\d)/g, "$1 ");
        return `+${countryCode} ${spacedNumber}`;
    }

    // If the number doesn't match expected patterns, return sanitized version
    // This handles edge cases like extensions or unusual formats
    return sanitized;
};
